/**
 * authenticationReducer.js
 * Talkroute
 *
 * Created by Vel Genov on 12/8/18
 * Copyright (c) 2018 Talkroute. All right reserved.
 */
import { IFeature } from "@models/Account.models";
import { UserPermissionsData } from "@services/dataClasses";
import { REHYDRATE } from "redux-persist";

import userConstants from "../constants/userConstants";

export interface IAuthenticationReducer {
  userName: string | null,
  isUserAuthenticated: boolean,
  newLogin: boolean,
  accessToken: string | null,
  refreshToken: string | null,
  tokenExpiresAt: number | null,
  authError: string | null,
  webrtcUserName: string | null,
  webrtcPassword: string | null,
  firstName: string | null,
  lastName: string | null,
  companyName: string | null,
  userPermissions: UserPermissionsData | null,
  userPermissionsPending: boolean,
  userTokenReady: boolean,
  userId: number | null,
  moxtraAccessToken: string | null,
  moxtraAccessTokenExpDate: number | null,
  isRefreshingToken: boolean,
  accountId: number | null,
  userRole: null | "Account Admin",
  permittedFeatures: IFeature[],
  callHistoryLastVisited: number,
  refreshTokenStatus: RefreshTokenStatus,
  isAccessTokenValid: boolean,
}

export enum RefreshTokenStatus {
  FETCHING,
  VALID_TOKEN_AVAILABLE,
  NO_TOKEN_AVAILABLE
}

const initialState: IAuthenticationReducer = {
  userName: null,
  isUserAuthenticated: false,
  newLogin: false,
  accessToken: null,
  refreshToken: null,
  tokenExpiresAt: null,
  authError: null,
  webrtcUserName: null,
  webrtcPassword: null,
  firstName: null,
  lastName: null,
  companyName: null,
  userPermissions: null,
  userPermissionsPending: false,
  userTokenReady: false,
  userId: null,
  moxtraAccessToken: null,
  moxtraAccessTokenExpDate: null,
  isRefreshingToken: false,
  accountId: null,
  userRole: null,
  permittedFeatures: [],
  callHistoryLastVisited: 0,
  isAccessTokenValid: false,
  refreshTokenStatus: RefreshTokenStatus.NO_TOKEN_AVAILABLE
};

/**
 * Called to process authentication actions
 *
 * @param {*} state
 * @param {*} action
 */
export default function authentication(state: IAuthenticationReducer = initialState, action): IAuthenticationReducer {
  switch (action.type) {
    case REHYDRATE:
      // A redux-persist action. Called immediately after launch. TalkroutePhoneNumber objects are restored as generic Object by default.
      if (action.payload !== undefined) {
        return {
          ...state,
          accessToken: action.payload.authentication.accessToken,
          refreshToken: action.payload.authentication.refreshToken,
          tokenExpiresAt: action.payload.authentication.tokenExpiresAt,
          userName: action.payload.authentication.userName,
          isUserAuthenticated: action.payload.authentication.isUserAuthenticated,
          webrtcUserName: action.payload.authentication.webrtcUserName,
          webrtcPassword: action.payload.authentication.webrtcPassword,
          firstName: action.payload.authentication.firstName,
          lastName: action.payload.authentication.lastName,
          companyName: action.payload.authentication.companyName,
          userId: action.payload.authentication.userId,
          moxtraAccessToken: action.payload.authentication.moxtraAccessToken,
          accountId: action.payload.authentication.accountId,
          permittedFeatures: action.payload.authentication.permittedFeatures,
          newLogin: false,
          authError: null,
          isAccessTokenValid: action.payload.authentication.userTokenReady,
          isRefreshingToken: false, // should be removed in a future release
          refreshTokenStatus: action.payload.authentication.refreshToken ?
            action.payload.authentication.refreshTokenStatus || RefreshTokenStatus.VALID_TOKEN_AVAILABLE :
            RefreshTokenStatus.NO_TOKEN_AVAILABLE
        } as IAuthenticationReducer;
      }

      return {
        ...state,
      };
    case userConstants.LOGIN_REQUEST:
      // The authentication process has started
      return {
        ...state,
        authError: null,
      };
    case userConstants.LOGIN_SUCCESS:
      // The authentication process was completed successfully
      return {
        ...state,
        userName: action.payload.user,
        isUserAuthenticated: true,
        tokenExpiresAt: action.payload.tokenExpiresAt,
        webrtcUserName: action.payload.webrtcUserName,
        webrtcPassword: action.payload.webrtcPassword,
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        companyName: action.payload.companyName,
        userId: action.payload.userId,
        accountId: action.payload.accountId,
        newLogin: true,
        authError: null,
      };
    case userConstants.IMPERSONATE_LOGIN_SUCCESS:
      return {
          ...state,
          userId: action.payload.impersonateUserId,
          userName: action.payload.userEmail,
          firstName: action.payload.userFirstName,
          lastName: action.payload.userLastName,
          accountId: action.payload.accountId,
          companyName: action.payload.companyName,
      };
    case userConstants.TOKEN_REFRESHED:
      return {
        ...state,
        isUserAuthenticated: true,
        authError: null,
      };
    case userConstants.LOGIN_FAILURE:
      // The authentication process has failed
      return {
        ...state,
        authError: action.payload.error,
      };
    case userConstants.CLEAR_AUTH_ERROR:
      // Clear any authentication errors
      return {
        ...state,
        authError: null,
      };
    case userConstants.UPDATE_REFRESH_TOKEN_STATUS: {
      return {
        ...state,
        refreshTokenStatus: action.payload
      }
    }
    case userConstants.SET_ACCESS_TOKEN_IS_VALID: {
      return {
        ...state,
        isAccessTokenValid: action.payload
      }
    }
    case userConstants.REFRESH_TOKEN_PENDING:
      return {
        ...state,
        isRefreshingToken: action.payload
      }
    case userConstants.GET_USER_PERMISSIONS:
      // Set the user permissions
      return {
        ...state,
        userPermissions: action.payload.data.userPermissions,
      };
    case userConstants.USER_TOKEN_READY:
      return {
        ...state,
        userTokenReady: true,
      };
    case userConstants.SET_PERMITTED_FEATURES:
      return {
        ...state,
        permittedFeatures: action.payload,
      }
    case userConstants.SET_MOXTRA_ACCESS_TOKEN:
      return {
        ...state,
        moxtraAccessToken: action.payload.moxtraAccessToken,
        moxtraAccessTokenExpDate: action.payload.moxtraAccessTokenExpDate
      };
    case userConstants.USER_TOKEN_NOT_READY:
      return {
        ...state,
        userTokenReady: false,
      };
    case userConstants.GET_USER_PERMISSIONS_PENDING:
      return {
        ...state,
        userPermissionsPending: action.payload
      };
    case userConstants.SET_USER_ROLE:
      return {
        ...state,
        userRole: action.payload
      }
    case userConstants.SET_CALL_HISTORY_LAST_VIEWED:
      return {
        ...state,
        callHistoryLastVisited: action.payload
      }
    default:
      return state;
  }
}
